import React from "react";
import { Link } from "react-router-dom";
import { PARK, NAV_LINKS } from "@/lib/site";
import { useContent } from "@/lib/content";
import { Facebook, Instagram, Star } from "lucide-react";

const Footer = () => {
  const c = useContent();
  const phone = c("contact.phone", PARK.phone);
  const email = c("contact.email", PARK.email);
  const addressLines = [
    c("contact.address1", PARK.address.lines[0]),
    c("contact.address2", PARK.address.lines[1]),
    c("contact.address3", PARK.address.lines[2]),
  ];
  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0A0A0A] text-[#EAE0D0] pt-24 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <img
              src={PARK.logo}
              alt="Walnut Lakes"
              className="h-32 md:h-40 w-auto logo-blend -ml-2 mb-4"
              loading="lazy"
            />
            <p className="mt-8 text-[#D8CDBA] max-w-md text-base leading-relaxed">
              Walnut Lakes Holiday Park is a family-run, adults-only retreat in
              the Lincolnshire fens. Three lakeside lodges &amp; 45 pitches. Open
              Mid-March through end of November.
            </p>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#E4B2A8] mb-5">Visit</p>
            <div className="space-y-1 text-[#D8CDBA] text-sm">
              {addressLines.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
              <div>United Kingdom</div>
            </div>
            <div className="mt-6 space-y-2 text-sm">
              <div>
                <a
                  href={`tel:${phone.replace(/\s/g, "")}`}
                  className="link-underline text-[#F5EFE6]"
                  data-testid="footer-phone"
                >
                  {phone}
                </a>
              </div>
              <div>
                <a
                  href={`mailto:${email}`}
                  className="link-underline text-[#F5EFE6]"
                  data-testid="footer-email"
                >
                  {email}
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#E4B2A8] mb-5">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    className="text-sm text-[#D8CDBA] link-underline"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="/policies"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-park-policies"
                >
                  Park Policies
                </a>
              </li>
              <li>
                <Link
                  to="/lodges-lincolnshire"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-lodges-lincolnshire"
                >
                  Lodges in Lincolnshire
                </Link>
              </li>
              <li>
                <Link
                  to="/camping-lincolnshire"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-camping-lincolnshire"
                >
                  Camping in Lincolnshire
                </Link>
              </li>
              <li>
                <Link
                  to="/fishing-holidays-lincolnshire"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-fishing-holidays-lincolnshire"
                >
                  Fishing Holidays
                </Link>
              </li>
              <li>
                <Link
                  to="/dog-friendly-holidays-lincolnshire"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-dog-friendly-lincolnshire"
                >
                  Dog-Friendly Holidays
                </Link>
              </li>
              <li>
                <Link
                  to="/glamping-lincolnshire"
                  className="text-sm text-[#D8CDBA] link-underline"
                  data-testid="footer-link-glamping-lincolnshire"
                >
                  Glamping in Lincolnshire
                </Link>
              </li>
            </ul>

            {/* Social + Reviews */}
            <p className="overline text-[#E4B2A8] mt-10 mb-4">Follow &amp; Review</p>
            <div className="flex items-center gap-3">
              {PARK.socials?.instagram && (
                <a
                  href={PARK.socials.instagram}
                  target="_blank"
                  rel="noopener"
                  aria-label="Instagram"
                  data-testid="footer-social-instagram"
                  className="w-9 h-9 rounded-full border border-[#B5A99E]/40 flex items-center justify-center text-[#EAE0D0] hover:bg-[#B76E79] hover:border-[#B76E79] hover:text-white transition-all"
                >
                  <Instagram size={14} />
                </a>
              )}
              {PARK.socials?.facebook && (
                <a
                  href={PARK.socials.facebook}
                  target="_blank"
                  rel="noopener"
                  aria-label="Facebook"
                  data-testid="footer-social-facebook"
                  className="w-9 h-9 rounded-full border border-[#B5A99E]/40 flex items-center justify-center text-[#EAE0D0] hover:bg-[#B76E79] hover:border-[#B76E79] hover:text-white transition-all"
                >
                  <Facebook size={14} />
                </a>
              )}
              {PARK.socials?.googleReviews && (
                <a
                  href={PARK.socials.googleReviews}
                  target="_blank"
                  rel="noopener"
                  aria-label="Read our Google reviews"
                  data-testid="footer-social-google"
                  className="w-9 h-9 rounded-full border border-[#B5A99E]/40 flex items-center justify-center text-[#EAE0D0] hover:bg-[#B76E79] hover:border-[#B76E79] hover:text-white transition-all font-bold text-xs"
                  title="Google reviews"
                >
                  G
                </a>
              )}
              {PARK.socials?.pitchup && (
                <a
                  href={PARK.socials.pitchup}
                  target="_blank"
                  rel="noopener"
                  aria-label="See us on Pitchup"
                  data-testid="footer-social-pitchup"
                  className="w-9 h-9 rounded-full border border-[#B5A99E]/40 flex items-center justify-center text-[#EAE0D0] hover:bg-[#B76E79] hover:border-[#B76E79] hover:text-white transition-all font-bold text-[10px]"
                  title="Pitchup listing"
                >
                  P
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-20 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs uppercase tracking-[0.24em] text-[#B5A99E]">
            © {new Date().getFullYear()} {PARK.fullName}
          </p>
          <p className="text-xs uppercase tracking-[0.24em] text-[#B5A99E]">
            Adults only · Rod licence required · Season: {PARK.season}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
